import type { MetadataRegistryEntry } from '@compass/client/types/metadata_registry';

/**
 * Per-org metadata advanced filters (AQ-496).
 *
 * The advanced filters rendered on the QC list + analytics pages are the keys in
 * METADATA_FILTER_KEYS that also exist in the org's searchable metadata registry
 * (domain="evaluation", searchable=true), in METADATA_FILTER_KEYS order.
 */

// TODO: move this list to UI config.
// Allow-list + display order of advanced-filter keys (registry slug form).
const METADATA_FILTER_KEYS = [
  'team-name',
  'campaign-name',
  'skill-name',
  'contact-role',
  'call-reason',
  'call-events',
];

/**
 * Intersects registry entries with METADATA_FILTER_KEYS, keeping the list's
 * order. Registry keys not in the list are dropped.
 */
export const pickMetadataFilterDefinitions = (
  entries: MetadataRegistryEntry[],
): MetadataRegistryEntry[] => {
  const byKey = new Map(entries.map(entry => [entry.key, entry]));
  return METADATA_FILTER_KEYS.flatMap(key => byKey.get(key) ?? []);
};

// Widget taxonomy from entry.ui_control.widget. Anything unrecognized (or empty)
// falls back to the searchable multiselect.
export type MetadataFilterWidget =
  | 'multiselect-with-search'
  | 'single-select'
  | 'async-search-only'
  | 'free-text';

export const METADATA_ASYNC_MIN_QUERY_LENGTH = 2;

/**
 * Resolves the effective widget for a registry entry, defaulting to the
 * searchable multiselect when unset/unrecognized.
 */
export const resolveMetadataFilterWidget = (entry: MetadataRegistryEntry): MetadataFilterWidget => {
  switch (entry.ui_control?.widget) {
    case 'single-select':
      return 'single-select';
    case 'async-search-only':
      return 'async-search-only';
    case 'free-text':
      return 'free-text';
    case 'multiselect-with-search':
    default:
      return 'multiselect-with-search';
  }
};

export const getMetadataFilterLabel = (entry: MetadataRegistryEntry): string =>
  entry.label?.trim() || entry.key || '';

/** Static options declared on the registry entry, if any. */
export const getMetadataStaticOptions = (entry: MetadataRegistryEntry): string[] =>
  (entry.ui_control?.options ?? []).filter(Boolean);

/**
 * Formats a metadata value for DISPLAY only — the raw value is still what gets
 * sent to the filter (backend matching is exact on the stored value). Splits on
 * underscores and capitalizes each word ("status_check" → "Status Check",
 * "refill" → "Refill"). Applied to dropdown option labels and selected chips
 * across all widgets and both value sources (registry `ui_control.options` and
 * API/ClickHouse-distinct).
 */
export const displayMetadataValue = (value: string): string =>
  value
    ? value
        .split('_')
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : value;
